import Entity from '../../../../../../foundation/domain/Entity';
import EntityId from '../../../../../../foundation/domain/EntityId';
import Lesson from '../lesson/Lesson';
import ExerciseTemplate from '../exerciseTemplate/ExerciseTemplate';
import Registry from '../../../../../global/Registry';
import * as voca from 'voca';

export default class Exercise extends Entity<Exercise> {

	public constructor (id: EntityId<Exercise>, private lessonId: EntityId<Lesson>, private content: string, private templateId: EntityId<ExerciseTemplate>, private contents: string[]) {
		super(id);
	}

	public static create (lessonId: EntityId<Lesson>, templateId: EntityId<ExerciseTemplate>) {
		return new this(EntityId.create<Exercise>(), lessonId, '', templateId, this.generateDefaultContents(templateId));
	}

	public getLessonId () {
		return this.lessonId;
	}

	public getContent () {
		return this.content;
	}

	public getTemplateId () {
		return this.templateId;
	}

	public getContents () {
		return Exercise.generateDefaultContents(this.getTemplateId()).map((value, index) => this.contents[index] !== undefined ? this.contents[index] : '');
	}

	public hasContentAtIndex (index: number) {
		return this.contents.length > index;
	}

	public getQuestion () {
		return Registry.getInstance().exerciseTemplateRepository.getById(this.templateId).getQuestion(this);
	}

	public getAnswer () {
		return Registry.getInstance().exerciseTemplateRepository.getById(this.templateId).getAnswer(this);
	}

	public getFirstAnswer () {
		return typeof this.getAnswer() === 'string' ? this.getAnswer().split('\n')[0] : this.getAnswer();
	}

	public getExplanation () {
		return Registry.getInstance().exerciseTemplateRepository.getById(this.templateId).getExplanation(this);
	}

	public updateLessonId (lessonId: EntityId<Lesson>) {
		this.lessonId = lessonId;
		return this;
	}

	public getUsedExerciseIds () {
		return this.getTemplateId().getValue() !== 13 ? [] : this.getContents()[2].split(',').filter(value => value !== '').map(value => new EntityId<Exercise>(Number(value)));
	}

	public getSlug () {
		return Registry.getInstance().lessonRepository.getById(this.getLessonId()).getSlug() + '/exercise/' + this.getId().getValue() + '/' + voca.slugify(voca.stripTags(this.textAnswer(this.getFirstAnswer())));
	}

	private textAnswer (answer: any): any {
		if (answer && answer.props && answer.props.children) {
			return this.textAnswer(answer.props.children);
		}

		return answer;
	}

	public getEditingSlug () {
		return Registry.getInstance().lessonRepository.getById(this.getLessonId()).getSlug() + '/exercise/' + this.getId().getValue() + '/edit';
	}

	public updateContent (content: string) {
		this.content = content;
		return this;
	}

	public updateContentAtIndex(index: number, value: string) {
		const numberOfVariables = voca.countSubstrings(Registry.getInstance().exerciseTemplateRepository.getById(this.templateId).getContent(), '{{');

		for (let i = 0; i < numberOfVariables; i++) {
			if (this.contents[i] === undefined) {
				this.contents[i] = '';
			}
		}

		this.contents[index] = value;
		return this;
	}

	private static generateDefaultContents (templateId: EntityId<ExerciseTemplate>) {
		switch (templateId.getValue()) {
			case 1:
				return ['true', 'For any <Math>A</Math> and <Math>B</Math>, if <Math>A \\subset B</Math>, then <Math>|B| > |A|</Math>', ''];
			case 2:
				return ['In a neutral atom, the amount of', 'electrons and protons\nelectrons and neutrons\nelectrons and nucleons\nneutrons and protons', 'are equal.', ''];
			case 3:
				return ['<Math>\\mathbb{R} = </Math>', '1', '<Math>\\mathbb{I} \\cup \\mathbb{N}</Math>\n<Math>\\mathbb{I} \\cup \\mathbb{Q}</Math>\n<Math>\\mathbb{I} \\cup \\mathbb{Z}</Math>\n<Math>\\mathbb{N} \\cup \\mathbb{Q}</Math>\n<Math>\\mathbb{N} \\cup \\mathbb{Z}</Math>\n<Math>\\mathbb{Q} \\cup \\mathbb{Z}</Math>', '', ''];
			case 4:
				return ['What is the name of the element with atomic number 1?', 'hydrogen', ''];
			case 5:
				return ['Write <Math>sum(4, 5)</Math> using infix notation', '4+5', ''];
			case 6:
				return ['Vietnam', 'TUBS', 'Wikimedia Commons', 'CC BY-SA 3.0', 'https://commons.wikimedia.org/wiki/File%3AVietnam_in_its_region.svg', 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/22/Vietnam_in_its_region.svg/1280px-Vietnam_in_its_region.svg.png', 'What is the <strong>name</strong> of this country?', 'Vietnam', ''];
			case 7:
				return ['30\n45\n60\n90\n120\n135\n150\n180\n210\n225\n240\n270\n300\n315\n330\n360', '<Math>*value*^\\circ=</Math>', '<Math>\\frac{1}{6}\\pi\\text{ rad}</Math>\n<Math>\\frac{1}{4}\\pi\\text{ rad}</Math>\n<Math>\\frac{1}{3}\\pi\\text{ rad}</Math>\n<Math>\\frac{1}{2}\\pi\\text{ rad}</Math>\n<Math>\\frac{2}{3}\\pi\\text{ rad}</Math>\n<Math>\\frac{3}{4}\\pi\\text{ rad}</Math>\n<Math>\\frac{5}{6}\\pi\\text{ rad}</Math>\n<Math>\\pi\\text{ rad}</Math>\n<Math>\\frac{7}{6}\\pi\\text{ rad}</Math>\n<Math>\\frac{5}{4}\\pi\\text{ rad}</Math>\n<Math>\\frac{4}{3}\\pi\\text{ rad}</Math>\n<Math>\\frac{3}{2}\\pi\\text{ rad}</Math>\n<Math>\\frac{5}{3}\\pi\\text{ rad}</Math>\n<Math>\\frac{7}{4}\\pi\\text{ rad}</Math>\n<Math>\\frac{11}{6}\\pi\\text{ rad}</Math>\n<Math>2\\pi\\text{ rad}</Math>', '', ''];
			case 8:
				return ['In a closed system, entropy is always decreasing.', '0', 'True\nFalse', ''];
			case 9:
				return ['What is the chemical formula for water?', 'H2O', ''];
			case 10:
				return ['', ''];
			case 11:
				return ['', '0', ''];
			case 12:
				return ['', ''];
			case 13:
				return ['', '', ''];
			default:
				throw new Error('No default content for exercise template ' + templateId);
		}
	}
}


// WEBPACK FOOTER //
// ./code/project/modules/lessons/domain/aggregates/exercise/Exercise.ts